import { format } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

import { FALLBACK_VALUE } from "../constants/common";

export const isValidDateString = (dateString: string): boolean => {
    return !isNaN(Date.parse(dateString));
};

/**
 * Defines a date format that can be passed into various functions to help properly format dates
 */
export enum DateFormat {
    ISO_8601_CALENDAR_DATE_FORMAT = "yyyy-MM-dd",
    LONG_DATE_FORMAT = "MMMM dd, yyyy",
}
/**
 * Defines a IANA timezone that can be passed to format in specific timezone
 */
export enum Timezone {
    EST = "America/New_York",
    CST = "America/Chicago",
    MST = "America/Denver",
    PST = "America/Los_Angeles",
    UTC = "UTC",
    GMT = "GMT",
    JST = "Asia/Tokyo",
    IST = "Asia/Kolkata",
    CET = "Europe/Paris",
}

/**
 * Formats the given date into the optionally given format, or returns "-" if it is unable to
 * @param toFormat The date we would like to format
 * @param format The format we would like to format it into. Defaults to the ISO_8601 standard for calendar dates without times
 * @param formatTimezone The timezone you would like to format it into
 * @returns A UI friendly string we can display
 */
export const formatDate = (
    toFormat: Date | string | undefined,
    dateFormat: DateFormat = DateFormat.ISO_8601_CALENDAR_DATE_FORMAT,
    formatTimezone?: string,
): string => {
    try {
        if (formatTimezone && toFormat) {
            return formatInTimeZone(
                new Date(toFormat),
                formatTimezone,
                dateFormat,
            );
        } else {
            return toFormat
                ? format(new Date(toFormat), dateFormat)
                : FALLBACK_VALUE.INVALID_DATE_LABEL;
        }
    } catch (error) {
        console.error(
            `Error formatting date ${String(toFormat)} in format ${dateFormat}`,
            error,
        );
        return toFormat ? String(toFormat) : FALLBACK_VALUE.INVALID_DATE_LABEL;
    }
};

export const formatDateTime = (date: string | undefined | null): string => {
    if (date === undefined || date === null) {
        return "date undefined";
    }
    try {
        const parsedDate = new Date(date);
        if (isNaN(parsedDate.getTime())) {
            return "date cannot be parsed";
        }
        return parsedDate.toLocaleString();
    } catch {
        return "date cannot be parsed";
    }
};

/**
 * A helper function to generate a sort function that can be passed to [].sort(). e.g. if you have an object with a parsable date field
 * interface dateObject { dateField: Date | string | number }, you can sort it by calling dateObject[].sort(dateSort("dateField")).
 * @param key The key for the date parsable field we want to sort upon
 * @param isAscending Should we sort the dates in ascending order, e.g. 1/1, 1/2, 1/3. Default to false / a descending list
 * @returns A function that can be passed to the generic collections sort method
 */
export function dateSort<
    T extends Partial<Record<K, string | number | Date>>,
    K extends keyof T,
>(key: K, isAscending: boolean = false): (a: T, b: T) => number {
    return (a: T, b: T) => {
        const dateA = a[key] ? new Date(a[key]!) : new Date();
        const dateB = b[key] ? new Date(b[key]!) : new Date();
        return (dateA.getTime() - dateB.getTime()) * (isAscending ? 1 : -1);
    };
}
